import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { SuppliersFormDialog } from './SuppliersFormDialog';
import { SuppliersRowActions } from './SuppliersRowActions';
import { useSuppliersList } from './useSuppliers';
import type { Supplier } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const SuppliersPage = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);

  const query = useSuppliersList({ active_only: false, page, per_page: perPage });
  const rows = query.data?.rows ?? [];
  const total = query.data?.meta?.total ?? 0;

  const canManage = hasPermission(user, 'supplier.manage');

  const columns = useMemo<MasterTableColumn<Supplier>[]>(
    () => [
      {
        key: 'display_name',
        header: t('efs.masters.suppliers.fields.display_name', { ns: 'masters' }),
        cell: (row) => row.display_name,
      },
      {
        key: 'legal_name',
        header: t('efs.masters.suppliers.fields.legal_name', { ns: 'masters' }),
        cell: (row) => <span className="text-sm text-efs-text-muted">{row.legal_name}</span>,
      },
      {
        key: 'active',
        header: t('efs.masters.suppliers.fields.active', { ns: 'masters' }),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t('efs.common.status.retired', { defaultValue: '無効' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <SuppliersRowActions row={row} />,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.suppliers.title', { ns: 'masters' })}
        subtitle={t('efs.masters.suppliers.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.masters.actions.create', { ns: 'masters' })}
            </Button>
          ) : null
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="suppliers" params={{ active_only: false }} />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.company_id}
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <SuppliersFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default SuppliersPage;
